/*
  1. Creating connection with GraphQL
  2. Handling and showing GraphQL and network errors.
*/

// packages block
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, NextLink, Operation, from } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
// components block
import { Alert } from "../components/common/Alert";
// utils and constants block
import {
  ACCOUNT_LOCKED,
  AUTH_TOKEN,
  CONFLICT_EXCEPTION,
  INVALID_OR_EXPIRED_TOKEN_MESSAGE, NOT_FOUND_EXCEPTION, PRECONDITION_FAILED_EXCEPTION, REQUEST_NOT_FOUND,
  TOKEN_NOT_FOUND
} from "../constants";
import { ErrorException } from "../interfaceTypes";
import { endSession, isSessionRejection, rejectionReason } from "../utils";

const authMiddleware = new ApolloLink((operation: Operation, forward: NextLink) => {
  const token = localStorage.getItem(AUTH_TOKEN);

  operation.setContext({
    headers: {
      authorization: `Bearer ${token}`,
    },
  });

  return forward(operation);
});

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_API_BASE_URL}/graphql`
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    // The session is over: the login page says why, so no toast for this or its siblings.
    const rejection = graphQLErrors
      .map(({ extensions }) => (extensions?.exception as ErrorException | undefined)?.response)
      .find(isSessionRejection)

    if (rejection) {
      endSession(rejectionReason(rejection.code))
      return
    }

    graphQLErrors.map(
      ({ extensions, message }) => {
        // Only the sign-in widget can get this, and it shows the lock inline instead.
        if (message === ACCOUNT_LOCKED) return null

        if (extensions) {
          const { exception } = extensions;

          if (exception) {
            const { response } = exception as ErrorException

            if (response) {
              const { error, response: errorResponse } = response

              if (error && error !== REQUEST_NOT_FOUND && error !== NOT_FOUND_EXCEPTION && error !== CONFLICT_EXCEPTION) {
                if (error === TOKEN_NOT_FOUND) {
                  Alert.error(INVALID_OR_EXPIRED_TOKEN_MESSAGE)
                } else {
                  Alert.error(error)
                }
              }

              if (errorResponse) {
                const { error: responseError, message } = errorResponse;

                if (message && message !== REQUEST_NOT_FOUND && message !== NOT_FOUND_EXCEPTION && message !== CONFLICT_EXCEPTION) {
                  Alert.error(message)
                } else if (responseError && responseError !== REQUEST_NOT_FOUND && responseError !== NOT_FOUND_EXCEPTION && responseError !== CONFLICT_EXCEPTION && responseError === PRECONDITION_FAILED_EXCEPTION) {
                  Alert.error(responseError)
                }
              }
            }
          }
        }

        return null
      }
    );
  }

  if (networkError) Alert.error(networkError.message);
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  connectToDevTools: true,
  link: from([authMiddleware, errorLink, httpLink]),
});

export default client;